import { useCallback } from "react";
import { useAnalytics } from "../analytics/context";
import { consumeMovedParam, wasMovedThisLoad } from "../movedQueryParams";
import { stripBlank } from "../utils";
import {
  AppState,
  IdPAuthorizationParams,
  IdPRedirectLoginOptions,
  LoginWithIdPOptions,
} from "./types";
import { buildCallbackUrl, extractUrlParameters } from "./urlParams";

/**
 * Returns a function that turns login options and the current URL into an authorize request:
 * the app state that rides through the IdP round trip and the authorize query parameters.
 * We have multiple parameters that we need to extract from the URL and from the options passed to the function.
 */
export function usePrepareLogin(
  audience: string
): (opts: LoginWithIdPOptions) => Promise<IdPRedirectLoginOptions> {
  const analytics = useAnalytics();

  return useCallback(
    async (opts: LoginWithIdPOptions): Promise<IdPRedirectLoginOptions> => {
      // Check if prompt=login is present in current URL
      // https://auth0.com/docs/authenticate/login/max-age-reauthentication
      // Check all the defined URL parameters that will modify the login flow.
      const params = extractUrlParameters(new URLSearchParams(window.location.search));
      // We need to restore the session if the user already has any analytics data set.
      if (params.ext_anonymousId || params.ext_gaClientId || params.ext_gaSessionId) {
        analytics.restore({
          anonymousId: params.ext_anonymousId,
          gaClientId: params.ext_gaClientId,
          gaSessionId: params.ext_gaSessionId,
        });
      }
      const sess = await analytics.save();
      const screenHint = opts.screen_hint || params.screen_hint || "login";
      // The token is moved from the URL to sessionStorage in index.html before analytics load
      // (to avoid leaking it to 3rd-party analytics), so it is no longer present as a URL param.
      // Fall back to the stored value and consume it so a stale token cannot leak into a later
      // login in the same tab. Missing this fallback caused INC-18719: the teammate-invite token
      // was stripped from the URL but this callback still only read it from the URL, so the
      // account-linking call was skipped and invitees fell through to standalone account creation.
      // Consume (read + remove) the stored token only when it isn't already on the URL/opts AND it
      // was freshly stripped from the URL on THIS page load. The freshness gate (wasMovedThisLoad)
      // prevents a leftover token — from an abandoned/failed reseller flow, or the retry write-back
      // in tryRedirectingUser — from attaching to a later, unrelated login in the same tab
      // (CLRF-2268 M1). A token that genuinely belongs to this login was in the URL on this load,
      // so index.html stripped it this load and the gate passes.
      const sessionToken =
        !opts.token && !params.token && wasMovedThisLoad("token") ? consumeMovedParam("token") : "";
      const token = opts.token || params.token || sessionToken;
      const prompt = params.prompt;
      const loginHint = opts.email || params.email;
      // Query parameters that are used for tracking purposes are passed in then add them to the callback.
      const redirect_uri = buildCallbackUrl(window.location.origin, params);
      let appState: AppState = stripBlank({
        ...sess, // Add the analytics session information to the app state
        token: token,
        screenHint: screenHint,
        fromUnifiedAccountLink: opts.fromUnifiedAccountLink,
        fromUnifiedAccountSwitching: opts.fromUnifiedAccountSwitching,
        fromReseller: opts.fromReseller,
      });
      let authorizationParams: IdPAuthorizationParams = stripBlank({
        audience: audience,
        login_hint: loginHint,
        redirect_uri: redirect_uri,
        screen_hint: screenHint,
        prompt,
      });
      if (Object.keys(sess).length > 0) {
        authorizationParams = stripBlank({
          ...authorizationParams,
          "ext-anonymousId": sess.anonymousId,
          "ext-gaClientId": sess.gaClientId,
          "ext-gaSessionId": sess.gaSessionId,
        });
        appState = stripBlank({
          ...appState,
          anonymousId: sess.anonymousId,
          gaClientId: sess.gaClientId,
          gaSessionId: sess.gaSessionId,
        });
      }
      return {
        appState: appState,
        authorizationParams: authorizationParams,
      };
    },
    [audience, analytics]
  );
}
